import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserPlus } from 'lucide-react';

const LOGO_URL = '/logo.png';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const extractErrorMessage = (err) => {
  const detail = formatApiErrorDetail(err?.response?.data?.detail);
  if (detail) return detail;
  if (err?.response?.status) {
    const code = err.response.status;
    if (code === 400) return 'Please double-check the details you entered.';
    if (code === 429) return 'Too many attempts. Please wait a few minutes and try again.';
    if (code >= 500) return `Our server hit a snag (${code}). Please try again in a moment.`;
    return `Request failed (${code}). Please try again.`;
  }
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('network') || msg.includes('failed to fetch')) {
    return "Can't reach Cravitoo right now. Check your internet connection and try again.";
  }
  if (msg.includes('timeout') || err?.code === 'ECONNABORTED') {
    return "Request timed out. Please try again.";
  }
  return err?.message ? `Registration failed: ${err.message}` : 'Registration failed. Please try again.';
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    // Public self-sign-up is hard-locked to employee. Vendor / Corporate Admin
    // / Site Admin / Super Admin / Master Admin accounts are provisioned via
    // an admin-controlled invitation flow. The backend enforces this too —
    // see backend/routers/auth.py::register and the role-escalation test
    // suite (tests/test_auth_role_escalation.py).
    role: 'employee',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Always send role=employee — the server will reject anything else.
      const user = await register(formData.email, formData.password, formData.name, 'employee');
      
      switch (user.role) {
        case 'employee':
          navigate('/employee/dashboard');
          break;
        default:
          navigate('/');
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
            <img src={LOGO_URL} alt="Cravitoo" className="h-20 w-auto object-contain" />
          </Link>
          <h1 className="font-heading text-3xl font-semibold text-text-primary mb-2">Create Account</h1>
          <p className="text-text-secondary">Join the Cravitoo platform today</p>
        </div>

        <div className="bg-card border border-border-light rounded-2xl p-8 shadow-sm">
          <form onSubmit={handleSubmit} data-testid="register-form">
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary mb-2" htmlFor="name">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                data-testid="register-name-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-border-light rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 bg-background"
                placeholder="John Doe"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary mb-2" htmlFor="email">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                data-testid="register-email-input"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-border-light rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 bg-background"
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary mb-2" htmlFor="password">
                Password
              </label>
              <input
                type="password"
                id="password"
                data-testid="register-password-input"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-4 py-3 border border-border-light rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 bg-background"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>

            <div className="mb-6">
              <p
                data-testid="register-role-note"
                className="text-xs text-text-muted bg-background border border-border-light rounded-lg p-3"
              >
                Sign-ups on this page create an <strong>Employee</strong> account
                for your company. Vendor and Admin accounts are issued by
                Cravitoo via a secure invitation — please contact your account
                manager.
              </p>
            </div>

            {error && (
              <div data-testid="register-error" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <UserPlus className="h-5 w-5" />
                  <span>Create Account</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-text-secondary text-sm">
              Already have an account?{' '}
              <Link to="/login" data-testid="register-login-link" className="text-primary hover:text-primary-hover font-medium transition-all duration-200">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;